// ✅ Updated AdminDashboard.jsx to include custom_orders count
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
// import SendNotificationButton from "../../components/SendNotificationButton";
import SendNotificationToCustomers from "../../components/SendNotificationToCustomers";
import useSaveFcmToken from "../../hooks/useSaveFcmToken";
import AddAdmin from "../../components/AddAdmin";

const AdminDashboard = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    activePlans: 0,
    pendingDeliveries: 0,
    totalRevenue: 0,
    customOrders: 0,
  });
  const navigate = useNavigate();

  // Auto-save FCM token for admin users
  useSaveFcmToken();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/admin/login");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();

        if (idTokenResult.claims.role === 'admin') {
          setAdmin({
            uid: user.uid,
            email: user.email,
            name: user.displayName,
            role: 'admin',
            permissions: idTokenResult.claims.permissions || []
          });
        } else {
          navigate("/admin/login");
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const subscriptionsSnap = await getDocs(collection(db, "subscriptions"));
        const customOrdersSnap = await getDocs(collection(db, "custom_orders"));

        const activePlans = subscriptionsSnap.docs.filter(
          (doc) => doc.data().status === "active" && doc.data().paymentStatus === "paid"
        );

        const revenue = activePlans.reduce((sum, doc) => {
          const data = doc.data();
          // Prefer 'amount' if present, else fallback to 'price'
          const value = data.amount !== undefined ? parseFloat(data.amount) : parseFloat(data.price);
          return sum + (isNaN(value) ? 0 : value);
        }, 0);

        const pendingDeliveries = activePlans.reduce((count, doc) => {
          const deliveryData = doc.data().deliveryStatus || {};
          return count + Object.values(deliveryData).filter((val) => val === "pending").length;
        }, 0);

        const pendingCustomOrders = customOrdersSnap.docs.filter(doc => doc.data().status === "pending");

        setStats({
          users: usersSnap.size,
          activePlans: activePlans.length,
          pendingDeliveries,
          totalRevenue: revenue,
          customOrders: pendingCustomOrders.length,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {admin?.name}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-green-700 mb-6">📊 Admin Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
            <h2 className="text-sm font-semibold text-gray-500">Total Users</h2>
            <p className="text-3xl font-bold text-green-700 mt-2">👥 {stats.users}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
            <h2 className="text-sm font-semibold text-gray-500">Active Plans</h2>
            <p className="text-3xl font-bold text-green-700 mt-2">📦 {stats.activePlans}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
            <h2 className="text-sm font-semibold text-gray-500">Pending Deliveries</h2>
            <p className="text-3xl font-bold text-green-700 mt-2">⏳ {stats.pendingDeliveries}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
            <h2 className="text-sm font-semibold text-gray-500">Total Revenue</h2>
            <p className="text-3xl font-bold text-green-700 mt-2">
              ₹ {stats.totalRevenue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
            <h2 className="text-sm font-semibold text-gray-500">New Custom Orders</h2>
            <p className="text-3xl font-bold text-green-700 mt-2">📩 {stats.customOrders}</p>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-green-100">
          <h2 className="text-xl font-bold text-green-700 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/admin/subscriptions')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">📦</div>
              <h3 className="font-semibold text-green-700">Manage Subscriptions</h3>
              <p className="text-sm text-gray-600">View and manage all subscriptions</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/custom-orders')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">📩</div>
              <h3 className="font-semibold text-green-700">Custom Orders</h3>
              <p className="text-sm text-gray-600">View & assign custom orders</p>
            </button>

            <button 
              onClick={() => navigate('/admin/custom-orders-enhanced')}
              className="p-4 border border-orange-200 rounded-lg hover:bg-orange-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">💰</div>
              <h3 className="font-semibold text-orange-700">Enhanced Custom Orders</h3>
              <p className="text-sm text-gray-600">Edit delivery charges & manage orders</p>
            </button>

            <button 
              onClick={() => navigate('/admin/customers')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">👥</div>
              <h3 className="font-semibold text-green-700">Manage Customers</h3>
              <p className="text-sm text-gray-600">View and manage all customers</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/assign-deliveries')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">🚚</div>
              <h3 className="font-semibold text-green-700">Assign Deliveries</h3>
              <p className="text-sm text-gray-600">Assign deliveries to partners</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/delivery-tracker')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">📍</div>
              <h3 className="font-semibold text-green-700">Delivery Tracker</h3>
              <p className="text-sm text-gray-600">Track and manage deliveries</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/delivery-partners')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">👨‍💼</div>
              <h3 className="font-semibold text-green-700">Delivery Partners</h3>
              <p className="text-sm text-gray-600">Manage delivery partners</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/analytics')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">📊</div>
              <h3 className="font-semibold text-green-700">Analytics</h3>
              <p className="text-sm text-gray-600">View business insights</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/reports')}
              className="p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">📈</div>
              <h3 className="font-semibold text-blue-700">Subscription Reports</h3>
              <p className="text-sm text-gray-600">Detailed subscription analytics & exports</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/coupons')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">🎟️</div>
              <h3 className="font-semibold text-green-700">Manage Coupons</h3>
              <p className="text-sm text-gray-600">View and manage all coupons</p>
            </button>
            
            <button 
              onClick={() => navigate('/admin/bonus-settings')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">💰</div>
              <h3 className="font-semibold text-green-700">Bonus Settings</h3>
              <p className="text-sm text-gray-600">Set delivery partner bonus & base</p>
            </button>

            <button 
              onClick={() => navigate('/admin/management')}
              className="p-4 border border-purple-200 rounded-lg hover:bg-purple-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">👑</div>
              <h3 className="font-semibold text-purple-700">Admin Management</h3>
              <p className="text-sm text-gray-600">Add and manage admin users</p>
            </button>

            <button 
              onClick={() => navigate('/admin/notifications')}
              className="p-4 border border-orange-200 rounded-lg hover:bg-orange-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">🔔</div>
              <h3 className="font-semibold text-orange-700">Notifications</h3>
              <p className="text-sm text-gray-600">Send push notifications to customers</p>
            </button>

            <button 
              onClick={() => navigate('/admin/settings')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">⚙️</div>
              <h3 className="font-semibold text-green-700">Admin Settings</h3>
              <p className="text-sm text-gray-600">Manage app settings & service areas</p>
            </button>

            <button 
              onClick={() => navigate('/admin/plans')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">💡</div>
              <h3 className="font-semibold text-green-700">Edit Subscription Plans</h3>
              <p className="text-sm text-gray-600">Quickly edit plan prices & details</p>
            </button>

            <button 
              onClick={() => navigate('/admin/product-interest')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-all text-left cursor-pointer"
            >
              <div className="text-2xl mb-2">🆕</div>
              <h3 className="font-semibold text-green-700">Product Interest</h3>
              <p className="text-sm text-gray-600">View interest in upcoming products</p>
            </button>

            <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
              <div className="text-2xl mb-2">🔔</div>
              <h3 className="font-semibold text-blue-700">Test Notifications</h3>
              <p className="text-sm text-gray-600 mb-3">Send test push notifications</p>
              {/* <SendNotificationButton /> */}
            </div>
          </div>
        </div>

        <SendNotificationToCustomers />

                {/* Notifications Management */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-green-100">
          <h2 className="text-xl font-bold text-green-700 mb-4">🔔 Notifications Management</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Send to All Customers</h3>
              <p className="text-gray-600 mb-4">
                Send promotional offers, announcements, and updates to all your customers via push notifications.
              </p>
            </div>
            <div>
              {/* <SendNotificationToCustomers /> */}
            </div>
          </div>
        </div>

        {/* Admin Management */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-green-100">
          <h2 className="text-xl font-bold text-green-700 mb-4">👑 Admin Management</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Admin</h3>
              <p className="text-gray-600 mb-4">
                Add new administrators to the system. They will be able to access the admin panel and manage the platform.
              </p>
            </div>
            <div>
              <AddAdmin onAdminAdded={() => {
                // Optionally refresh data or show success message
                console.log("✅ New admin added successfully!");
              }} />
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-green-100">
          <h2 className="text-xl font-bold text-green-700 mb-4">Recent Activity</h2>
          <div className="text-center py-8 text-gray-500">
            <p>No recent activity to display</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
